import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const FavoritesContext = createContext(null);

export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user || typeof user !== "object") return;
    setLoading(true);
    try {
      const { data } = await api.get("/favorites");
      setFavorites(data);
    } catch {
      setFavorites([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && typeof user === "object") {
      refresh();
    } else {
      setFavorites([]);
    }
  }, [user, refresh]);

  const isFavorite = useCallback(
    (slug, idx) =>
      favorites.some((f) => f.category_slug === slug && f.prompt_index === idx),
    [favorites]
  );

  const toggle = useCallback(
    async (slug, idx, meta) => {
      const currently = favorites.some(
        (f) => f.category_slug === slug && f.prompt_index === idx
      );
      if (currently) {
        // Optimistic remove
        setFavorites((prev) =>
          prev.filter((f) => !(f.category_slug === slug && f.prompt_index === idx))
        );
        try {
          await api.delete(`/favorites/${slug}/${idx}`);
        } catch {
          refresh();
        }
        return { added: false };
      } else {
        // Optimistic add
        const optimistic = {
          id: `tmp-${slug}-${idx}`,
          category_slug: slug,
          category_name: meta?.category_name || slug,
          category_icon: meta?.category_icon || "package",
          category_image_icon: meta?.category_image_icon,
          prompt_index: idx,
          prompt_title: meta?.prompt_title || "",
          prompt_body: meta?.prompt_body || "",
          created_at: new Date().toISOString(),
        };
        setFavorites((prev) => [optimistic, ...prev]);
        try {
          await api.post("/favorites", { category_slug: slug, prompt_index: idx });
          refresh();
          return { added: true };
        } catch (e) {
          // Roll back optimistic add and surface error
          setFavorites((prev) => prev.filter((f) => f.id !== optimistic.id));
          const detail = e?.response?.data?.detail;
          const msg = typeof detail === "string" ? detail : "Could not save favourite";
          return { added: false, error: msg };
        }
      }
    },
    [favorites, refresh]
  );

  return (
    <FavoritesContext.Provider value={{ favorites, loading, isFavorite, toggle, refresh }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export const useFavorites = () => useContext(FavoritesContext);
